/**
 * RemindersPanel: the signed-in admin's own call centre reminders (handoffs waiting, lists to review,
 * campaigns ending, people nobody has touched). Click one to open where it points (linkPath) and mark it read;
 * the cross dismisses it. Reminders only ever go to callers and staff, never to the people being called.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { IconBell, IconBellOff, IconX, IconChevronRight } from '@tabler/icons-react';
import { Stack, Group, Text, Paper, Badge, Switch, Box, Tooltip } from '@/components/ui';
import ActionIcon from '@/components/ui/ActionIcon';
import { useCallCentreAdminService, type CallCentreReminderView } from '@actschurch/ccms-sdk';
import { toast } from '@/components/ui/providers/AlertProvider';
import { SectionCard, InlineEmpty, cssColour, useCallCentreSettings, callCentreErrorMessage, callCentreKeys, relativeStamp, formatStampDateTime } from '../components';

export function RemindersPanel() {
  const admin = useCallCentreAdminService();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [includeRead, setIncludeRead] = useState(false);
  const settings = useCallCentreSettings();

  const { data, isLoading, error } = useQuery({
    queryKey: callCentreKeys.reminders(includeRead),
    queryFn: () => admin.getReminders(includeRead),
    refetchInterval: 120_000,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: callCentreKeys.remindersRoot() });

  const markRead = useMutation({
    mutationFn: (id: number) => admin.markReminderRead(id),
    onSuccess: invalidate,
  });
  const dismiss = useMutation({
    mutationFn: (id: number) => admin.dismissReminder(id),
    onSuccess: invalidate,
    onError: (err) => toast.error("Couldn't dismiss the reminder", callCentreErrorMessage(err)),
  });

  const open = (r: CallCentreReminderView) => {
    if (!r.readAt) markRead.mutate(r.reminderID);
    const link = r.linkPath?.trim();
    if (!link) return;
    if (/^https?:\/\//i.test(link)) {
      window.open(link, '_blank', 'noopener');
      return;
    }
    const path = link.startsWith('/') ? link : `/${link}`;
    // Console paths open here; anything else is a caller portal path (for example /referrals).
    const portal = settings.data?.portalBaseUrl?.replace(/\/+$/, '');
    if (!path.startsWith('/app/') && !path.startsWith('/admin/') && portal) window.open(`${portal}${path}`, '_blank', 'noopener');
    else navigate(path);
  };

  const items = data?.items ?? [];

  return (
    <SectionCard
      icon={<IconBell size={18} />}
      color="orange"
      title={
        <Group gap="xs" wrap="nowrap">
          <span>Reminders</span>
          {data && data.unread > 0 && (
            <Badge size="sm" color="red" variant="filled" styles={{ root: { textTransform: 'none' } }}>
              {data.unread}
            </Badge>
          )}
        </Group>
      }
      description="Things waiting for you. Only staff and callers get reminders."
      helpId="callcentre.dashboard.reminders"
      actions={<Switch size="xs" label="Show read" checked={includeRead} onChange={(e) => setIncludeRead(e.currentTarget.checked)} />}
    >
      {error ? (
        <Text size="sm" c="red">
          {callCentreErrorMessage(error, "Couldn't load your reminders.")}
        </Text>
      ) : isLoading ? (
        <Text size="sm" c="dimmed">
          Loading
        </Text>
      ) : items.length === 0 ? (
        <InlineEmpty icon={<IconBellOff size={22} />} title="Nothing waiting" description="Reminders show here when something needs you." />
      ) : (
        <Stack gap={6} mah={360} style={{ overflowY: 'auto' }}>
          {items.map((r) => (
            <Paper key={r.reminderID} withBorder radius="md" p="xs" style={{ cursor: r.linkPath ? 'pointer' : undefined, opacity: r.readAt ? 0.7 : 1 }} onClick={() => open(r)}>
              <Group justify="space-between" wrap="nowrap" gap="xs" align="flex-start">
                <Group gap="xs" wrap="nowrap" align="flex-start" style={{ minWidth: 0 }}>
                  <Box mt={6} style={{ width: 8, height: 8, borderRadius: 4, flexShrink: 0, backgroundColor: r.readAt ? 'transparent' : cssColour(r.color, 'orange') }} />
                  <Stack gap={0} style={{ minWidth: 0 }}>
                    <Text size="sm" fw={r.readAt ? 400 : 600} lineClamp={1}>
                      {r.title}
                    </Text>
                    {r.body && (
                      <Text size="xs" c="dimmed" lineClamp={2}>
                        {r.body}
                      </Text>
                    )}
                    <Tooltip label={formatStampDateTime(r.dueAt)} withArrow>
                      <Text size="xs" c="dimmed" span>
                        {r.typeName} · {relativeStamp(r.dueAt)}
                      </Text>
                    </Tooltip>
                  </Stack>
                </Group>
                <Group gap={2} wrap="nowrap">
                  {r.linkPath && <IconChevronRight size={14} color="var(--mantine-color-dimmed)" />}
                  <ActionIcon
                    size="sm"
                    variant="subtle"
                    color="gray"
                    aria-label="Dismiss"
                    loading={dismiss.isPending && dismiss.variables === r.reminderID}
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      dismiss.mutate(r.reminderID);
                    }}
                  >
                    <IconX size={14} />
                  </ActionIcon>
                </Group>
              </Group>
            </Paper>
          ))}
        </Stack>
      )}
    </SectionCard>
  );
}
